import styled from 'styled-components'

import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { useExperimentState } from 'experiments/hooks/useExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { OfferModalDetails } from 'offers/types'
import { getRemainingOfferTime } from 'offers/utils'
import { blinkStyles } from 'productTemplate/components/ProductPicker/blinkStyles'
import { useProductTemplateContext } from 'productTemplate/components/ProductTemplateContextProvider'
import {
  getDiscountPercentage,
  getUnderRetailLabel,
} from 'productTemplate/utils/under-retail-utils'
import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import Button from 'shared/components/Button'
import { SVGIcon } from 'shared/components/Icons'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { ProductQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, fonts } from 'shared/lib'
import { formatMoneyObject } from 'shared/lib/formatMoneyObject'
import { sizes } from 'shared/lib/media'
import { ISizePickerOptionVariant } from 'shared/types/ISizePickerOption'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getActiveOffers, setIsOffersModalOpen } from 'store/offersSlice'

type OfferButtonState = {
  offerButtonText: string
  offerModalDetails: OfferModalDetails
  offerExpiration: {
    display: string
    isLessThanADay: boolean
    expired: boolean
  }
}

const TP = 'productTemplate.components.ProductPicker.BuyButtonRow'

const getISPrice = (optionVariant?: ISizePickerOptionVariant) => {
  if (!optionVariant || !optionVariant?.instantShipLowestPriceCents?.display) {
    return null
  }
  return optionVariant.instantShipLowestPriceCents.display
}

const getOptionVariantPrice = (optionVariant?: ISizePickerOptionVariant) => {
  if (!optionVariant) {
    return null
  }
  if (
    optionVariant?.lowestPriceCents?.localizedValue &&
    optionVariant?.lowestPriceCents?.localizedValue <
      (optionVariant?.instantShipLowestPriceCents?.localizedValue || Infinity)
  ) {
    return optionVariant.lowestPriceCents.display
  }
  return optionVariant?.instantShipLowestPriceCents?.display
}

const getBuyButtonText = (
  isMarketPricing: boolean,
  isHideFastestToYou: boolean,
  optionVariant?: ISizePickerOptionVariant,
) => {
  const selectedOptionVariantPrice = (() => {
    if (isMarketPricing) {
      if (isHideFastestToYou) {
        return optionVariant?.lowestPriceCents?.localizedValue
          ? optionVariant?.lowestPriceCents?.display
          : null
      } else {
        return getOptionVariantPrice(optionVariant)
      }
    } else {
      return getISPrice(optionVariant)
    }
  })()

  if (selectedOptionVariantPrice) {
    const priceNoDecimals = selectedOptionVariantPrice.replace(/\.\d\d$/, '')

    return t(`${TP}.buyNew`, {
      defaultValue: 'Buy new - {lowestPriceCents}',
      lowestPriceCents: priceNoDecimals,
    })
  }

  return t(`${TP}.outOfStock`, 'Out of stock')
}

const BuyButtonRow = () => {
  const isMarketPricing = useFeatureFlag(FeatureFlag.MARKET_PRICING)
  const isHideFastestToYou = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_PDP_HIDE_FASTEST_TO_YOU)
  const isOffersEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_OFFERS)
  const enableExperiments = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const enableUnderRetailExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_UNDER_RETAIL_EXPERIMENT,
  )
  const enableAllInclusivePricingCopyExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_ALL_INCLUSIVE_PRICING_COPY_EXPERIMENT,
  )
  const webFCUnderRetailExperiment: EXPERIMENT_NAME = 'web_fc_under_retail'
  const webFCAllInclusivePricingExperiment: EXPERIMENT_NAME = 'web_fc_all_inclusive_pricing_v2'
  const dispatch = useAppDispatch()
  const experimentState = useExperimentState()
  const isMobile = useMediaQuery({ maxWidth: sizes.large })
  const { isApparel, selectedSize, setActiveProductModal, productTemplate, userOffersForPT } =
    useProductTemplateContext()
  const { country: selectedCountryCode } = useShoppingRegionContext()
  const isShoppingRegionUS = selectedCountryCode === 'US'
  const activeOffers = useAppSelector((state) => getActiveOffers(state))
  const [offerButtonState, setOfferButtonState] = useState<OfferButtonState>({
    offerButtonText: '',
    offerModalDetails: {} as OfferModalDetails,
    offerExpiration: { display: '', isLessThanADay: false, expired: false },
  })
  const newSizeOptionIsDefective = selectedSize?.new?.shoeCondition === 'new_with_defects'
  const displayOfferCTA =
    isOffersEnabled &&
    isShoppingRegionUS &&
    !isApparel &&
    productTemplate?.isOfferable &&
    selectedSize?.new &&
    !newSizeOptionIsDefective

  const allInclusivePricingCopyExperimentGroup =
    enableExperiments &&
    enableAllInclusivePricingCopyExperiment &&
    experimentState[webFCAllInclusivePricingExperiment]
      ? experimentState[webFCAllInclusivePricingExperiment].groupAssigned
      : 'control'

  const underRetailExperimentGroup =
    enableExperiments && enableUnderRetailExperiment && experimentState[webFCUnderRetailExperiment]
      ? experimentState[webFCUnderRetailExperiment].groupAssigned
      : 'control'

  const displayAllInclusiveCopy =
    allInclusivePricingCopyExperimentGroup !== 'control' && isShoppingRegionUS

  const displayLabels =
    (displayOfferCTA && offerButtonState.offerExpiration.display) ||
    underRetailExperimentGroup !== 'control'

  const underRetailLabel = getUnderRetailLabel(
    underRetailExperimentGroup || '',
    getDiscountPercentage(
      productTemplate.localizedSpecialDisplayPriceCents?.value || 0,
      selectedSize?.new?.lowestPriceCents?.value || 0,
    ) || 0,
    selectedCountryCode,
  )

  const handleOfferCTAClick = (offerModalDetails: OfferModalDetails) => {
    // send tracking event
    if (offerModalDetails.operation === 'create') {
      sendTrackingEvent('OFFER_MAKE_TAP', {
        product_template_slug: offerModalDetails.slug,
        size: offerModalDetails.size?.display || '',
        location: 'pdp',
      })
    } else if (offerModalDetails.operation === 'edit') {
      if (offerModalDetails.offer) {
        sendTrackingEvent('OFFER_EDIT_TAP', {
          product_template_slug: offerModalDetails.slug,
          size: offerModalDetails.size?.display || '',
          offer_duration: offerModalDetails.offer.offerDuration,
          offer_amount: `${(offerModalDetails.offer.offerAmount.amountUsdCents || 0) / 100}`,
          pref_currency_offer_amount: `${(offerModalDetails.offer.offerAmount.amount || 0) / 100}`,
          pref_currency: offerModalDetails.offer.offerAmount.currency,
          buy_now: offerModalDetails.lowestPriceCents!.value,
          want_id: offerModalDetails.offer.id,
          location: 'pdp',
        })
      }
    }

    dispatch(setIsOffersModalOpen({ offerModalDetails, status: true }))
  }

  // Upon selected size change, update the Offer button related state
  useEffect(() => {
    const sizeValue = selectedSize?.size.value

    if (!sizeValue) return

    const offerItem =
      userOffersForPT?.sizes?.includes(sizeValue) &&
      activeOffers
        .find((offerList) => offerList.productTemplate.id === userOffersForPT.productTemplate.id)
        ?.items.find((item) => item.size.size === sizeValue)

    const offerButtonText = offerItem
      ? t(`${TP}.editOffer`, 'Edit Offer - {offerPrice}', {
          offerPrice: ` ${formatMoneyObject(offerItem.profileItemData.offer.offerAmount)}`,
        })
      : t(`${TP}.makeOffer`, 'Make Offer')

    const offerModalDetails: OfferModalDetails = offerItem
      ? {
          lowestPriceCents: {
            display: formatMoneyObject(offerItem.lowestPriceCents),
            value: offerItem.lowestPriceCents.amount!,
          },
          minimumOfferCents: productTemplate?.minimumOfferCents,
          name: productTemplate?.name,
          offer: offerItem.profileItemData.offer,
          openFrom: 'pdp',
          operation: 'edit',
          pictureUrl: productTemplate?.pictureUrl,
          productTemplateId: productTemplate?.id,
          size: selectedSize?.size,
          sizeCategory: productTemplate?.sizeCategory || '',
          slug: productTemplate?.slug,
        }
      : {
          lowestPriceCents: selectedSize?.new?.lowestPriceCents,
          minimumOfferCents: productTemplate?.minimumOfferCents,
          name: productTemplate?.name,
          openFrom: 'pdp',
          operation: 'create',
          pictureUrl: productTemplate?.pictureUrl,
          productTemplateId: productTemplate?.id,
          size: selectedSize?.size,
          sizeCategory: productTemplate?.sizeCategory || '',
          slug: productTemplate?.slug,
        }

    const offerExpiration = offerItem
      ? getRemainingOfferTime(offerItem.profileItemData.offer)
      : { display: '', isLessThanADay: false, expired: false }

    setOfferButtonState({
      offerButtonText,
      offerModalDetails,
      offerExpiration,
    })
  }, [selectedSize, userOffersForPT, activeOffers, productTemplate])

  return (
    <>
      <BuyButtonsWrapper $doubleColumn={!!displayOfferCTA}>
        <div>
          {displayLabels && (
            <div style={{ display: 'block', height: '26px' }}>
              {underRetailLabel && (
                <div>
                  <UnderRetailLabel>{underRetailLabel}</UnderRetailLabel>
                  <IconWrapper>
                    <SVGIcon name="underRetailArrow" size={6} margin="0 0 1px" />
                  </IconWrapper>
                </div>
              )}
            </div>
          )}
          <Button
            buttonType="primary2"
            $fill
            disabled={!selectedSize?.new}
            onClick={() => setActiveProductModal('new')}
            qaAttr={`${ProductQA.BuyNew}BuyNowButton`}
            data-testid="buy-new-button"
          >
            <ButtonLabel>
              {getBuyButtonText(isMarketPricing, isHideFastestToYou, selectedSize?.new)}
            </ButtonLabel>
          </Button>
        </div>
        {displayOfferCTA && (
          <div>
            {displayLabels && (
              <OfferExpiration
                className="offer-expiration"
                $urgent={
                  offerButtonState.offerExpiration.isLessThanADay ||
                  offerButtonState.offerExpiration.expired
                }
              >
                {offerButtonState.offerExpiration.display && (
                  <>
                    {' '}
                    {t(`${TP}.expires`, 'Offer expires ')}
                    <span>{offerButtonState.offerExpiration.display}</span>
                  </>
                )}
              </OfferExpiration>
            )}
            <Button
              buttonType="primary1"
              $fill
              onClick={() => handleOfferCTAClick(offerButtonState.offerModalDetails)}
              data-qa={`${
                offerButtonState.offerModalDetails.operation === 'create' ? 'Make' : 'Edit'
              }OfferButton`}
            >
              <span>{offerButtonState.offerButtonText}</span>
            </Button>
          </div>
        )}
      </BuyButtonsWrapper>
      {displayAllInclusiveCopy && !isMobile && (
        <FeesWrapper>
          <FeesLabel>{t(`${TP}.noAditionalFee`, 'No additional fees at checkout')}</FeesLabel>
        </FeesWrapper>
      )}
    </>
  )
}

const BuyButtonsWrapper = styled.div<{ $doubleColumn: boolean }>`
  gap: 20px 12px;
  display: grid;
  grid-template-columns: ${({ $doubleColumn }) => ($doubleColumn ? '1fr 1fr' : '1fr')};
  margin-top: 16px;
`

const OfferExpiration = styled.label<{ $urgent: boolean }>`
  display: block;
  font-weight: 400;
  font-size: 12px;
  color: ${({ $urgent }) => ($urgent ? colors.FC2_RED : colors.FC2_BLACK)};
  height: 26px;
  text-align: right;

  span {
    font-weight: 500;
  }
`

const FeesWrapper = styled.div`
  text-align: center;
  margin: 24px 0 40px;
`

const FeesLabel = styled.div`
  ${fonts.SUBTITLE_2};
  font-size: 12px;
  text-transform: none;
`

const UnderRetailLabel = styled.label`
  ${fonts.SUBTITLE_3};
  color: ${colors.FC2_RED};
  text-transform: none;
  font-weight: 400;
  margin-right: 5px;
  line-height: 120%;
`

const IconWrapper = styled.div`
  display: inline-block;
  ${blinkStyles}
`

const ButtonLabel = styled.div`
  height: 45px;
  display: inherit;
`

export default BuyButtonRow
